/**
 * Locale-specific URLs for the Apply Limit Banner "Learn more" link
 * Points to the Indeed Job Seeker Guidelines support article
 */

import { useLocale } from '../../../../../../shared/I18n';
import { JSTLandingScreen } from '../../../../../../shared/native-modules/tare/JSTLandingScreen';
import navigateTo from '../../../../../../shared/native-modules/tare/navigateTo';

import { useCallback, useMemo } from 'react';

const SUPPORT_ARTICLE_BASE_URL =
    'https://support.indeed.com/hc/{locale}/articles/360028540531-Indeed-Job-Seeker-Guidelines';

const SUPPORTED_LOCALES = new Set([
    'en-us',
    'ar',
    'cs',
    'da',
    'de',
    'el',
    'it',
    'en-au',
    'en-ca',
    'en-gb',
    'en-in',
    'es-es',
    'es-us',
    'fi',
    'fr',
    'fr-ca',
    'hu',
    'id',
    'he',
    'ja',
    'ko',
    'nl-be',
    'nl',
    'no',
    'pl',
    'pt-br',
    'pt-pt',
    'ro',
    'sv',
    'th',
    'tr',
    'uk',
    'vi',
    'zh-cn',
    'zh-tw',
]);

const DEFAULT_LOCALE = 'en-us';

/**
 * Gets the support article URL for the given locale.
 * Tries full locale (e.g., 'fr-ca'), then language-only (e.g., 'fr'), then falls back to 'en-us'.
 */
export const getSupportArticleUrl = (language?: string, country?: string): string => {
    if (!language) {
        return SUPPORT_ARTICLE_BASE_URL.replace('{locale}', DEFAULT_LOCALE);
    }

    const lowerLanguage = language.toLowerCase();
    const lowerCountry = country?.toLowerCase();

    // Try full locale first (e.g., 'fr-ca')
    if (lowerCountry) {
        const fullLocale = `${lowerLanguage}-${lowerCountry}`;
        if (SUPPORTED_LOCALES.has(fullLocale)) {
            return SUPPORT_ARTICLE_BASE_URL.replace('{locale}', fullLocale);
        }
    }

    // Try language-only (e.g., 'ja' from 'ja-jp')
    if (SUPPORTED_LOCALES.has(lowerLanguage)) {
        return SUPPORT_ARTICLE_BASE_URL.replace('{locale}', lowerLanguage);
    }

    // Fall back to default
    return SUPPORT_ARTICLE_BASE_URL.replace('{locale}', DEFAULT_LOCALE);
};

/**
 * Hook to get the locale-specific support article URL and navigation handler
 * for the Apply Limit Banner "Learn more" link
 */
export const useApplyLimitBannerUrl = () => {
    const locale = useLocale();

    const language = useMemo(() => locale.language ?? '', [locale.language]);
    const country = useMemo(() => locale.country ?? '', [locale.country]);
    const url = useMemo(() => getSupportArticleUrl(language, country), [language, country]);

    const handleOpenSupportArticle = useCallback(async () => {
        if (!url) {
            return;
        }

        try {
            await navigateTo(JSTLandingScreen.supportUrl(url), 'RnViewjob');
        } catch (error) {
            console.error('Failed to open support article:', error);
        }
    }, [url]);

    return { url, handleOpenSupportArticle };
};
